import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './providers/AuthProvider';
import { ROUTES } from './routes';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ChooseAppPage from './pages/ChooseAppPage';
import UserRoleManagementPage from './pages/UserRoleManagementPage';
import PartnerPortalPage from './pages/PartnerPortalPage';

function LogoutPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const ranRef = React.useRef(false);

  React.useEffect(() => {
    if (ranRef.current) return undefined;
    ranRef.current = true;

    void auth.logout();
    navigate(ROUTES.LOGIN, { replace: true });
  }, [auth.logout, navigate]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

function Guard({ children }) {
  const auth = useAuth();
  if (auth.loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!auth.isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const auth = useAuth();
  if (auth.loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to={ROUTES.LOGIN} replace />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route
          path={ROUTES.PARTNER}
          element={<Guard><PartnerPortalPage /></Guard>}
        />
        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicOnly>
              <LoginPage />
            </PublicOnly>
          }
        />
        <Route
          path={ROUTES.REGISTER}
          element={
            <PublicOnly>
              <RegisterPage />
            </PublicOnly>
          }
        />
        <Route path="/register" element={<Navigate to={ROUTES.REGISTER} replace />} />
        <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
        <Route
          path={ROUTES.CHANGE_PASSWORD}
          element={
            <Guard>
              <ChangePasswordPage />
            </Guard>
          }
        />
        <Route
          path={ROUTES.WORKSPACE}
          element={
            <Guard>
              <ChooseAppPage />
            </Guard>
          }
        />
        <Route
          path={ROUTES.USER_ROLE_MANAGEMENT}
          element={
            <Guard>
              <UserRoleManagementPage />
            </Guard>
          }
        />
        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
